'use client'
import { useChannelWithFallback } from '@/api/hooks'
import { EmptyCollectionCallout } from '@/app/components/collections/callouts'
import ModalManager from '@/app/components/collections/modal-manager'
import { getChannelGifsById } from '@/app/util/channel-api'
import ChannelTags from '@/components/carousel/channel-tags'
import ChannelGrid from '@/components/grid/channel-grid'
import Grid, { GridContext } from '@/components/grid/grid'
import UAParserContext from '@/context/ua-parser'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { ReactNode, useCallback, useContext, useState } from 'react'
import { desktopContent } from 'ui/src/constants'
import { useChannelChildren } from '../channel-children'
import Banner from '../components/banner'
import ChannelInfo from '../components/channel-info'
import ChannelOverlay from '../components/channel-overlay'
import ChannelPageHeader, { EditCollectionButton, ShareCollectionButton } from '../components/page-header'
import { desktopContainerCss } from '../constants'

const clientsideFetchLimit = 15

type Props = { channel: IChannel; initialGifs: IGif[]; addGifsButton: ReactNode }
const containerCss = 'flex flex-col gap-5 [&_section]:flex [&_section]:flex-col [&_section]:gap-2'
const SubChannelPageContents = ({ channel: _channel, initialGifs = [], addGifsButton }: Props) => {
    const { deviceType } = useContext(UAParserContext)

    let { data: channel = _channel } = useChannelWithFallback(_channel)
    const children = useChannelChildren(channel.id, channel.children)
    // channels can be managed by SWR / inifinite gifs in grids currently use this approach
    const [externalGifs, setExternalGifs] = useState<IGif[]>(initialGifs)
    // const hasCollections = !!channel.children?.length
    const hasGifs = !!externalGifs?.length

    const fetchGifs = useCallback(
        async (offset: number) => {
            const result = await getChannelGifsById(channel.id, {
                offset,
                limit: clientsideFetchLimit,
            })
            return result
        },
        [channel.id]
    )

    if (!channel) {
        return null
    }
    const getTrackingUrl = (page: number) => `/${channel.slug}/page/${page}`
    return (
        <GridContext.Provider value={{ gifs: externalGifs, setGifs: setExternalGifs }}>
            <ModalManager>
                <Banner channel={channel} />
                <div className={cn(containerCss, desktopContainerCss)}>
                    <ChannelInfo channel={channel} display_name={channel.display_name} />
                    <div className={cn(containerCss, 'md:flex-1')}>
                        <div className="flex flex-row justify-between">
                            <div className="hidden flex-col gap-3 px-1.5 py-px md:flex">
                                <ChannelPageHeader channel={channel} title={channel.display_name} />
                                <ChannelTags channel={channel} className="md:hidden" />
                            </div>
                            <div className="hidden shrink-0 gap-3 md:flex">
                                <ShareCollectionButton channel={channel} />
                                {addGifsButton}
                                <EditCollectionButton channel={channel} />
                            </div>
                        </div>
                        {!hasGifs && children.length === 0 && <EmptyCollectionCallout channel={channel} />}
                        <div className="my-3 flex flex-col gap-5">
                            <section>
                                <ChannelGrid key={`ChannelGrid-${channel.id}`} channel={channel} />
                            </section>
                            {hasGifs && (
                                <section>
                                    <h4>All the {channel.display_name} Content</h4>
                                    <Grid
                                        // this makes add gifs to collection work since the button is outside the grid
                                        setExternalGifs={setExternalGifs}
                                        externalGifs={externalGifs}
                                        columns={deviceType === 'desktop' ? 3 : undefined}
                                        width={deviceType === 'desktop' ? desktopContent : undefined}
                                        fetchGifs={fetchGifs}
                                        gaTrackingUrl={getTrackingUrl}
                                        key={`Grid-${channel.id}`}
                                        overlay={(props) => <ChannelOverlay {...props} channel={channel} />}
                                    />
                                </section>
                            )}
                        </div>
                    </div>
                </div>
            </ModalManager>
        </GridContext.Provider>
    )
}

export default SubChannelPageContents
